import { CloseCircleOutlined, DashOutlined } from '@ant-design/icons'
import { message, Popconfirm, Progress, Spin, Upload, UploadProps } from 'antd'
import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ContentWrapper, UploadWrapper, FileButton, FileLink } from './styles'
import { UploadFile } from 'antd/lib/upload/interface'
import DefaultJsonFormContext from '../../components/v2/default-json-form/context/default-json-form-context'
import { caseDeleteFile } from '../../store/appeals/actions'
import { ActivityTimelineContext } from '../../components/v2/activity-timeline'
import { deleteTimeLineItem } from '../../store/timeline/actions'
import FilePreview from '../../components/file-preview'
import { useAppDispatch } from '../../hooks/useAppDispatch/useAppDispatch'

interface UploadFilesProps {
  value?: any,
  schema?: any,
  onChange?: (value) => void,
  UISchema?: any,
}

const UploadFilesS3: React.FC<UploadFilesProps> = (props: UploadFilesProps) => {
  const { UISchema, value, schema, onChange } = props
  // @ts-ignore
  const { entityType, entityId } = useContext(DefaultJsonFormContext)
  const disabled = useMemo(() => Boolean(UISchema?.options?.disabled), [UISchema])
  const multiple = useMemo(() => Boolean(UISchema?.options?.multiple), [UISchema])
  const uploadLabel = UISchema?.options?.uploadLabel
  const maxSizeBytes = UISchema?.options?.maxSizeBytes
  const required = useMemo(() => Boolean(UISchema?.required || UISchema?.options?.required), [UISchema])
  const [isLoading, setIsLoading] = useState(false)
  const [isBorder, setIsBorder] = useState(false)
  const error = required && (!value || value?.length === 0)
  const dispatch = useAppDispatch()
  const { deleteTimelineItem } = useContext(ActivityTimelineContext)
  const [fileList, setFileList] = useState(value?.length ? value.filter(file => file !== null && file !== undefined).map(file => {
    // Очищает файлы от uid (из-за uid форма не проходит проверку на идентичность)
    const { uid, ...rest } = file.response?.files?.[0] || file
    return rest
  }) : [])

  const action = useMemo(() => {
    if (entityType && entityId) {
      if (entityType === 'hr') {
        return `/api/${entityType}/request/${entityId}/add-file`
      } else {
        return `/api/${entityType}/${entityId}/add-file`
      }
    }
    return `/api/v2/upload`
  }, [entityType, entityId])

  const handleOnChange = useCallback(data => {
    const { file, fileList: newFileList } = data
    const { status, error } = file
    const isDone = status === 'done'

    if (error) {
      message.error(`${file.name} не удалось загрузить`)
    }
    if (isDone) {
      message.success(`${file.name} загружен`)

      // Очищает файлы от uid перед передачей в onChange
      const cleanedFileList = newFileList.filter(loadedFile => loadedFile && (!loadedFile.status || loadedFile.status === 'done')).map(loadedFile => {
        const { uid, ...rest } = loadedFile.response?.files?.[0] || loadedFile
        return rest
      })

      onChange?.(cleanedFileList)
    }

    setIsLoading(newFileList.some(item => item.status === 'uploading'))
    setFileList(newFileList)
    setIsBorder(Boolean(newFileList?.length))
  }, [onChange])

  const removeHandler = useCallback(async file => {
    const id = file.id || file?.response?.files?.[0]?.id
    const newFileList = fileList
      .filter(f => f !== null && f !== undefined)
      .filter(f => id !== undefined
        ? f.response?.files?.[0]?.id !== id && f?.id !== id
        : f.uid !== file.uid)
      .map(f => {
        // Очищает файлы от uid перед передачей в onChange
        const { uid, ...rest } = f.response?.files?.[0] || f
        return rest
      })

    if (id !== undefined) {
      await dispatch(caseDeleteFile({ id }))
      dispatch(deleteTimeLineItem({ data: { id } }))
    }

    setFileList(newFileList)
    onChange?.(newFileList.filter(f => !f.status || f.status === 'done'))
  }, [onChange, dispatch, fileList])

  const defaultProps: UploadProps = useMemo(() => ({
    name: 'file',
    multiple: multiple,
    action: action,
    fileList: fileList,
    progress: {
      strokeColor: {
        '0%': '#108ee9',
        '100%': '#87d068',
      },
      strokeWidth: 3,
      format: percent => percent && `${parseFloat(percent.toFixed(2))}%`,
    },
    data: (_) => ({title: schema?.key || 'file'}),
    itemRender: (
      _originNode: ReactElement,
      file: UploadFile,
    ) => {
      const savedFile = file.response?.files?.[0] || file
      const uploading = file.status === 'uploading'
      const failed = file.status === 'error'
      return (
        <div>
          <ContentWrapper disabled={disabled || isLoading} error={error || failed} className='antd-input antd-input-container'>
            {savedFile.url ? (
              <FileLink href={savedFile.url} title={savedFile.name} target="_blank" rel="noopener noreferrer">{savedFile.name}</FileLink>
            ) : <FileLink as="span" title={file.name}>{file.name}</FileLink>}
            {!uploading && !failed && <FilePreview files={savedFile} />}
            {!disabled && !isLoading && (
              <Popconfirm title={`Удалить файл ${file.name}?`} onConfirm={() => removeHandler(file)}>
                <CloseCircleOutlined style={{ color: '#ff0000' }} />
              </Popconfirm>
            )}
          </ContentWrapper>
          {uploading && <Progress percent={Math.round(file.percent || 0)} size="small" />}
          {failed && <div role="alert" style={{ color: '#ff4d4f' }}>Не удалось загрузить файл. Попробуйте ещё раз.</div>}
        </div>
      )
    },
    beforeUpload: file => {
      if (maxSizeBytes && file.size > maxSizeBytes) {
        message.error(`Файл «${file.name}» больше ${Math.round(maxSizeBytes / 1000000)} МБ`)
        return Upload.LIST_IGNORE
      }
      setIsLoading(true)
    }
  }), [fileList, disabled, multiple, action, isLoading, error, removeHandler, schema?.key, maxSizeBytes])

  return (
    <UploadWrapper className='custom-upload-wrapper' isBorder={true}>
      <Upload {...defaultProps} disabled={disabled || isLoading} onChange={handleOnChange}>
        <FileButton
          disabled={disabled || isLoading}
          className='ant-input'
          aria-label={uploadLabel || 'Добавить файл'}
          title={uploadLabel || 'Добавить файл'}
        >
          {isLoading ? <Spin size='small'/> : <DashOutlined/>}
        </FileButton>
      </Upload>
    </UploadWrapper>
  )
}

export default memo(UploadFilesS3)
